import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Gift, Crown, X, Heart } from "lucide-react";
import client from "../lib/api";

const STORAGE_KEY = "soulmate_show_welcome_tokens";

/**
 * Welcome modal shown ONCE right after the user completes the onboarding
 * questionnaire. Explains the Free + Tokens + Premium model in the user's
 * own moment — when they're about to start exploring.
 *
 * Mounts on /discover and reads a localStorage flag set by Onboarding.jsx.
 */
const WelcomeTokensModal = () => {
  const [open, setOpen] = useState(false);
  const [tokens, setTokens] = useState(null);

  useEffect(() => {
    let active = true;
    let flagged = false;
    try { flagged = localStorage.getItem(STORAGE_KEY) === "1"; } catch { /* noop */ }
    if (!flagged) return;
    (async () => {
      try {
        const res = await client.get("/discovery-tokens");
        if (!active) return;
        setTokens(res.data);
        setOpen(true);
      } catch {
        // If the endpoint failed (older user, etc.), still show the modal
        // with the documented defaults — better UX than blocking on a
        // network hiccup.
        if (active) {
          setTokens({ available: 3, rules: { initial: 3, bonus_profile: 1, bonus_first_match: 1 } });
          setOpen(true);
        }
      }
    })();
    return () => { active = false; };
  }, []);

  const close = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* noop */ }
    setOpen(false);
  };

  if (!open || !tokens) return null;
  const available = tokens.available ?? 3;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8"
      data-testid="welcome-tokens-modal">
      {/* Backdrop */}
      <button
        aria-label="Fechar"
        onClick={close}
        className="absolute inset-0 bg-[#0C0E12]/80 backdrop-blur-md cursor-default"
      />
      {/* Card */}
      <div className="relative w-full max-w-lg bg-gradient-to-br from-[#1B1E26] via-[#161920] to-[#0C0E12] border border-[#C5A880]/30 rounded-3xl p-8 md:p-10 shadow-2xl shadow-black/40 animate-fade-in-up overflow-hidden">
        {/* Decorative glow */}
        <div className="absolute -top-32 -right-32 w-72 h-72 bg-[#C5A880]/15 rounded-full blur-3xl pointer-events-none" />

        <button
          onClick={close}
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 inline-flex items-center justify-center text-[#A29F9B] hover:text-[#F5F2EE] transition-colors"
          aria-label="Fechar"
          data-testid="welcome-tokens-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="relative">
          <div className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-[#C5A880]/10 border border-[#C5A880]/30 text-xs text-[#C5A880] tracking-wide">
            <Heart className="w-3.5 h-3.5" /> Bem-vindo(a) à Soulmatch
          </div>

          <h2 className="font-display text-3xl md:text-4xl leading-tight mb-3">
            Tens <span className="text-[#C5A880]">{available} fichas</span> à tua espera
          </h2>
          <p className="text-[#DCD7D2] leading-relaxed mb-6">
            Cada <strong>Ficha de Descoberta Profunda</strong> desbloqueia, para sempre,
            tudo o que precisas saber sobre <em>uma pessoa específica</em> — análise IA do
            par, perguntas para o 1º encontro e verificação pública.
          </p>

          <ul className="space-y-3 mb-6">
            <li className="flex gap-3 items-start">
              <div className="w-8 h-8 rounded-full bg-[#C5A880]/15 border border-[#C5A880]/30 inline-flex items-center justify-center shrink-0">
                <Gift className="w-3.5 h-3.5 text-[#C5A880]" />
              </div>
              <div>
                <p className="text-[#F5F2EE] text-sm leading-snug">
                  <strong>{available} fichas grátis</strong> · usa-as nas pessoas que te chamarem realmente a atenção
                </p>
                <p className="text-xs text-[#A29F9B] mt-0.5">A escolha consciente é sempre tua.</p>
              </div>
            </li>
            <li className="flex gap-3 items-start">
              <div className="w-8 h-8 rounded-full bg-[#C5A880]/15 border border-[#C5A880]/30 inline-flex items-center justify-center shrink-0">
                <Sparkles className="w-3.5 h-3.5 text-[#C5A880]" />
              </div>
              <div>
                <p className="text-[#F5F2EE] text-sm leading-snug">
                  <strong>+1 ficha</strong> ao completares o teu perfil <span className="text-[#A29F9B]">(3 fotos + bio cuidada)</span>
                </p>
                <p className="text-xs text-[#A29F9B] mt-0.5">Quanto mais te mostrares, mais cresce a tua descoberta.</p>
              </div>
            </li>
            <li className="flex gap-3 items-start">
              <div className="w-8 h-8 rounded-full bg-[#C5A880]/15 border border-[#C5A880]/30 inline-flex items-center justify-center shrink-0">
                <Heart className="w-3.5 h-3.5 text-[#C5A880]" />
              </div>
              <div>
                <p className="text-[#F5F2EE] text-sm leading-snug">
                  <strong>+1 ficha</strong> na tua primeira ligação mútua
                </p>
                <p className="text-xs text-[#A29F9B] mt-0.5">A vida recompensa quem se permite encontrar.</p>
              </div>
            </li>
          </ul>

          <div className="border-t border-white/10 pt-5 mb-6">
            <p className="text-xs text-[#A29F9B] leading-relaxed">
              <Crown className="w-3.5 h-3.5 text-[#C5A880] inline mr-1.5" />
              Quando quiseres ir além — <strong className="text-[#DCD7D2]">descobrir sem limite</strong>,
              ver quem te deu like sem precisar de match, ou usar filtros avançados — o
              <strong className="text-[#DCD7D2]"> Premium</strong> está lá (€7,99/mês ou €59,99/ano,
              cancela quando quiseres).
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={close}
              className="btn-primary inline-flex items-center justify-center gap-2 flex-1"
              data-testid="welcome-tokens-start"
            >
              <Sparkles className="w-4 h-4" /> Começar a descobrir
            </button>
            <Link
              to="/premium#fichas"
              onClick={close}
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="welcome-tokens-learn"
            >
              Saber mais
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeTokensModal;
